import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import {
  Activity,
  AlertCircle,
  ArrowRight,
  BarChart3,
  CheckCircle2,
  Eye,
  Loader2,
  MessageCircle,
  MousePointer,
  PlusCircle,
  Radio,
  Send,
  Users,
} from "lucide-react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts"

import { apiRequest, type ApiError } from "@/lib/api"
import { cn } from "@/lib/utils"
import type { Account, DashboardStats, Project, User } from "../types"

// Import UI components if they exist, or fallback to regular divs with classes
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"

type DashboardProps = {
  user: User
  account: Account
  currentProject: Project | null
}

type DashboardStatsResponse = {
  stats: DashboardStats
}

const pct = (ratio: number) => `${(ratio * 100).toFixed(1)}%`

const FUNNEL_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444"]

const Dashboard = ({ user, account, currentProject }: DashboardProps) => {
  const projectSlug = currentProject?.slug

  const { data, isLoading, isError, error } = useQuery<DashboardStatsResponse, ApiError>({
    queryKey: ["dashboardStats", projectSlug],
    queryFn: () =>
      apiRequest<DashboardStatsResponse>(
        `/api/projects/${encodeURIComponent(projectSlug || "")}/dashboard_stats`
      ),
    enabled: Boolean(projectSlug),
  })

  const stats = data?.stats

  const funnelData = stats
    ? [
        { name: "Enviados", value: stats.messages.sent },
        { name: "Entregues", value: stats.messages.delivered },
        { name: "Abertos", value: stats.messages.opened },
        { name: "Cliques", value: stats.messages.clicked },
      ]
    : []

  return (
    <div className="flex flex-col gap-8 pb-10">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Painel</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {account.name} • {currentProject?.name ?? "Nenhum projeto selecionado"}
          </p>
        </div>
        {currentProject && (
          <div className="flex items-center gap-3">
            <Button asChild variant="outline">
              <Link to={`/${projectSlug}/subscribers`}>
                <Users className="mr-2 h-4 w-4" />
                Inscritos
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to={`/${projectSlug}/contacts`}>
                <MessageCircle className="mr-2 h-4 w-4" />
                Contatos
              </Link>
            </Button>
            <Button asChild>
              <Link to={`/${projectSlug}/broadcasts`}>
                <PlusCircle className="mr-2 h-4 w-4" />
                Novo Broadcast
              </Link>
            </Button>
          </div>
        )}
      </div>

      {!currentProject ? (
        <div className="flex min-h-[400px] flex-col items-center justify-center rounded-xl border border-dashed bg-muted/20 p-8 text-center">
          <Activity className="mb-4 h-10 w-10 text-muted-foreground/50" />
          <h2 className="mb-1 text-lg font-semibold">Nenhum Projeto Selecionado</h2>
          <p className="max-w-sm text-sm text-muted-foreground">
            Selecione um projeto na barra lateral para ver as estatísticas do painel e gerenciar seus broadcasts.
          </p>
        </div>
      ) : null}

      {isLoading ? (
        <div className="flex min-h-[400px] items-center justify-center rounded-xl border bg-card/50">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : null}

      {isError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-900 dark:border-red-900/50 dark:bg-red-950/20 dark:text-red-200">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-500" />
            <h3 className="font-semibold">Falha ao carregar painel</h3>
          </div>
          <p className="mt-2 text-sm">{error?.message ?? "Ocorreu um erro inesperado."}</p>
        </div>
      ) : null}

      {stats ? (
        <div className="flex flex-col gap-8">
          {/* Top KPIs */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Inscritos Push</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats.subscribers.total.toLocaleString()}</div>
                <p className="mt-1 text-xs text-muted-foreground">
                  <span className="font-medium text-emerald-600 dark:text-emerald-400">
                    {stats.subscribers.active.toLocaleString()}
                  </span>{" "}
                  ativos agora
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Contatos do Facebook</CardTitle>
                <MessageCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats.contacts.total.toLocaleString()}</div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Total de contatos via Messenger
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Broadcasts Ativos</CardTitle>
                <Radio className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats.workflows.active.toLocaleString()}</div>
                <p className="mt-1 text-xs text-muted-foreground">
                  De {stats.workflows.total.toLocaleString()} broadcasts no total
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Mensagens Enviadas</CardTitle>
                <Send className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats.messages.sent.toLocaleString()}</div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Mensagens enviadas no total
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-8 lg:grid-cols-7">
            {/* Funnel Chart Section */}
            <Card className="lg:col-span-4">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Funil de Engajamento</CardTitle>
                    <CardDescription>
                      Resumo de entrega e interação de mensagens
                    </CardDescription>
                  </div>
                  <BarChart3 className="h-5 w-5 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] w-full pt-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={funnelData}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} opacity={0.5} />
                      <XAxis type="number" hide />
                      <YAxis
                        dataKey="name"
                        type="category"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "currentColor", opacity: 0.7 }}
                      />
                      <RechartsTooltip
                        cursor={{ fill: "var(--muted)", opacity: 0.2 }}
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div className="rounded-lg border bg-background p-3 shadow-md">
                                <p className="mb-1 font-medium">{payload[0].payload.name}</p>
                                <p className="text-2xl font-bold text-primary">
                                  {Number(payload[0].value).toLocaleString()}
                                </p>
                              </div>
                            )
                          }
                          return null
                        }}
                      />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={40}>
                        {funnelData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={FUNNEL_COLORS[index % FUNNEL_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Performance Rates */}
            <div className="flex flex-col gap-4 lg:col-span-3">
              <Card className="flex-1 bg-linear-to-br from-blue-50 to-blue-100/50 dark:from-blue-950/20 dark:to-blue-900/10">
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-2 text-blue-800 dark:text-blue-300">
                    <CheckCircle2 className="h-4 w-4" />
                    Taxa de Entrega
                  </CardDescription>
                  <CardTitle className="text-4xl text-blue-950 dark:text-blue-100">{pct(stats.rates.delivery_rate)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-blue-800/80 dark:text-blue-300/80">
                    {stats.messages.delivered.toLocaleString()} entregues com sucesso de {stats.messages.sent.toLocaleString()} enviados
                  </div>
                </CardContent>
              </Card>

              <Card className="flex-1 bg-linear-to-br from-green-50 to-green-100/50 dark:from-green-950/20 dark:to-green-900/10">
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-2 text-green-800 dark:text-green-300">
                    <Eye className="h-4 w-4" />
                    Taxa de Abertura
                  </CardDescription>
                  <CardTitle className="text-4xl text-green-950 dark:text-green-100">{pct(stats.rates.open_rate)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-green-800/80 dark:text-green-300/80">
                    {stats.messages.opened.toLocaleString()} aberturas únicas de {stats.messages.delivered.toLocaleString()} entregues
                  </div>
                </CardContent>
              </Card>

              <Card className="flex-1 bg-linear-to-br from-amber-50 to-amber-100/50 dark:from-amber-950/20 dark:to-amber-900/10">
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-2 text-amber-800 dark:text-amber-300">
                    <MousePointer className="h-4 w-4" />
                    Taxa de Cliques
                  </CardDescription>
                  <CardTitle className="text-4xl text-amber-950 dark:text-amber-100">{pct(stats.rates.click_rate)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-amber-800/80 dark:text-amber-300/80">
                    {stats.messages.clicked.toLocaleString()} cliques de {stats.messages.opened.toLocaleString()} abertos
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Quick Actions / Getting Started */}
          <div className="mt-4">
            <h3 className="mb-4 text-lg font-medium tracking-tight">Links Rápidos</h3>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Link
                to={`/${projectSlug}/broadcasts`}
                className="group flex flex-col justify-between rounded-xl border bg-card p-5 transition-all hover:border-primary/50 hover:shadow-xs"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Radio className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Gerenciar Broadcasts</h4>
                    <p className="text-sm text-muted-foreground">Criar ou editar sequências de mensagens</p>
                  </div>
                </div>
                <div className="mt-4 flex items-center text-sm font-medium text-primary opacity-0 transition-opacity group-hover:opacity-100">
                  Ver Broadcasts <ArrowRight className="ml-1 h-4 w-4" />
                </div>
              </Link>
              
              <Link
                to={`/${projectSlug}/subscribers`}
                className="group flex flex-col justify-between rounded-xl border bg-card p-5 transition-all hover:border-primary/50 hover:shadow-xs"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Users className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Ver Inscritos</h4>
                    <p className="text-sm text-muted-foreground">Gerencie seus segmentos de audiência</p>
                  </div>
                </div>
                <div className="mt-4 flex items-center text-sm font-medium text-primary opacity-0 transition-opacity group-hover:opacity-100">
                  Ver Audiência <ArrowRight className="ml-1 h-4 w-4" />
                </div>
              </Link>

              <Link
                to={`/${projectSlug}/contacts`}
                className="group flex flex-col justify-between rounded-xl border bg-card p-5 transition-all hover:border-primary/50 hover:shadow-xs"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <MessageCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Contatos do Facebook</h4>
                    <p className="text-sm text-muted-foreground">Gerencie seus contatos via Messenger</p>
                  </div>
                </div>
                <div className="mt-4 flex items-center text-sm font-medium text-primary opacity-0 transition-opacity group-hover:opacity-100">
                  Ver Contatos <ArrowRight className="ml-1 h-4 w-4" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      ) : null}

      {currentProject && !isLoading && !isError && !stats ? (
        <div className="flex min-h-[200px] items-center justify-center rounded-xl border bg-card p-6">
          <div className="flex flex-col items-center gap-2 text-center text-muted-foreground">
            <Activity className="h-8 w-8 opacity-50" />
            <p>Ainda não há estatísticas disponíveis para este projeto.</p>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default Dashboard
